import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { v4 } from 'uuid';

import { GcpModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { isDefined } from '../../../../utils/variables';
import { Icon } from '../../icon/icon';

interface GcpStatusIconProps {
  model: GcpModel;
  onToggleStatus?: (id: string) => void;
  t: TFunction;
}

export const GcpStatusIcon: React.FC<GcpStatusIconProps> = ({ model, onToggleStatus, t }) => {
  const theme = useTheme();

  const iconConfig = useMemo<{
    icon: IconProp;
    hoverIcon: IconProp;
    color: string;
    title?: string;
  }>(() => {
    switch (model.status) {
      case 'ENABLED':
        return {
          icon: ['fas', 'check-circle'],
          hoverIcon: ['fad', 'check-circle'],
          color: theme.color.green,
          title: t('gcpList.status.enabled', { ns: 'cloudProcessing' }),
        };
      case 'DISABLED':
        return {
          icon: ['fas', 'times-circle'],
          hoverIcon: ['fad', 'times-circle'],
          color: theme.color.gray.medium,
          title: t('gcpList.status.disabled', { ns: 'cloudProcessing' }),
        };
      case 'CHECKPOINT':
        return {
          icon: ['fas', 'crosshairs-simple'],
          hoverIcon: ['fad', 'crosshairs-simple'],
          color: theme.color.brand.dark,
          title: t('gcpList.status.checkpoint', { ns: 'cloudProcessing' }),
        };
      default:
        return {
          icon: ['fas', 'question-circle'],
          hoverIcon: ['fad', 'question-circle'],
          color: theme.color.gray.medium,
        };
    }
  }, [model.status, theme, t]);

  const key = useMemo(() => v4(), []);

  return (
    <Icon
      color={iconConfig.color}
      icon={iconConfig.icon}
      key={key}
      style={{ cursor: onToggleStatus ? 'pointer' : 'default' }}
      title={iconConfig.title}
      onClick={onToggleStatus && isDefined(model.id) ? () => onToggleStatus(model.id!) : undefined}
      onHoverStyle={{ icon: iconConfig.hoverIcon }}
    />
  );
};
